
import React, { useState } from 'react';
import { Shield } from 'lucide-react';

// Components
import Hero from './components/Hero';
import VideoSection from './components/VideoSection';
import ModuleSection from './components/ModuleSection';
import AudienceSection from './components/AudienceSection';
import WaitlistSection from './components/WaitlistSection';
import BonusSection from './components/BonusSection';
import Navbar from './components/Navbar';
import LegalModals from './components/LegalModals';

const App: React.FC = () => {
  const [modalConfig, setModalConfig] = useState<{isOpen: boolean, type: 'terms' | 'lgpd' | 'status' | 'reference', title: string}>({
    isOpen: false,
    type: 'terms',
    title: ''
  });

  const openModal = (type: 'terms' | 'lgpd' | 'status' | 'reference', title: string) => {
    setModalConfig({ isOpen: true, type, title });
  };

  const closeModal = () => {
    setModalConfig({ ...modalConfig, isOpen: false });
  };

  return (
    <div className="min-h-screen bg-black bg-grid">
      <Navbar onReferenceClick={() => openModal('reference', 'Referência Estratégica')} />
      
      <main>
        <Hero />
        
        <VideoSection />
        
        <section id="vision" className="py-24 px-4 sm:px-6 lg:px-8 border-y border-zinc-900 bg-black/50 backdrop-blur-sm relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 text-[#c17c0c] mb-4 px-3 py-1 bg-[#c17c0c]/10 border border-[#c17c0c]/20 rounded-full">
                  <Shield size={16} />
                  <span className="text-xs font-semibold uppercase tracking-widest">Protocolo War Room v3.1</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight uppercase italic">
                  Não apenas Dados. <br/>
                  <span className="text-[#c17c0c]">Decisões Prescritivas.</span>
                </h2>
                <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                  O Guru.voto é uma plataforma de elite que combina Ciência de Dados Geoespaciais e IA Generativa para transformar dados brutos em vitórias táticas. Diferente de sistemas de BI passivos, nós dizemos exatamente onde atacar, com quem se aliar e como vencer.
                </p>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 border border-zinc-800 rounded-lg bg-zinc-950/50 hover:border-[#c17c0c]/30 transition-all cursor-default group">
                    <h4 className="text-[#c17c0c] font-bold mb-1 group-hover:translate-x-1 transition-transform uppercase font-tactical">Granularidade H3</h4>
                    <p className="text-xs text-zinc-500">Hexágonos de ~0.1km² para precisão territorial absoluta.</p>
                  </div>
                  <div className="p-4 border border-zinc-800 rounded-lg bg-zinc-950/50 hover:border-[#c17c0c]/30 transition-all cursor-default group">
                    <h4 className="text-[#c17c0c] font-bold mb-1 group-hover:translate-x-1 transition-transform uppercase font-tactical">IA Multi-LLM</h4>
                    <p className="text-xs text-zinc-500">Orquestração de modelos SOTA para análise qualitativa.</p>
                  </div>
                  <div className="p-4 border border-zinc-800 rounded-lg bg-zinc-950/50 hover:border-[#c17c0c]/30 transition-all cursor-default group">
                    <h4 className="text-[#c17c0c] font-bold mb-1 group-hover:translate-x-1 transition-transform uppercase font-tactical">Compliance TSE</h4>
                    <p className="text-xs text-zinc-500">Totalmente adequado à resolução 23.677/2021.</p>
                  </div>
                  <div className="p-4 border border-zinc-800 rounded-lg bg-zinc-950/50 hover:border-[#c17c0c]/30 transition-all cursor-default group">
                    <h4 className="text-[#c17c0c] font-bold mb-1 group-hover:translate-x-1 transition-transform uppercase font-tactical">UX Imersiva</h4>
                    <p className="text-xs text-zinc-500">Dark Mode unificado com navegação de guerra.</p>
                  </div>
                </div>
              </div>
              
              <div className="relative group flex justify-center lg:justify-end">
                <div className="absolute -inset-1 bg-gradient-to-r from-[#c17c0c] to-amber-500 rounded-full blur opacity-10 group-hover:opacity-20 transition duration-1000"></div>
                <div className="relative w-full max-w-md aspect-square flex items-center justify-center">
                   {/* Abstract Hexagon Visualization */}
                   <div className="grid grid-cols-3 gap-2 transform rotate-12 opacity-80">
                      {[...Array(9)].map((_, i) => (
                        <div key={i} className={`w-20 h-20 sm:w-24 sm:h-24 hexagon-mask bg-gradient-to-br ${i === 4 ? 'from-[#c17c0c] to-amber-600 scale-110 z-10 shadow-[0_0_30px_rgba(193,124,12,0.4)]' : 'from-zinc-800 to-zinc-900 opacity-60'} flex items-center justify-center transition-all duration-500 hover:scale-105`}>
                           {i === 4 && <Shield className="text-black" size={32} />}
                        </div>
                      ))}
                   </div>
                   
                   {/* Floating Data Points */}
                   <div className="absolute top-10 right-10 bg-black/80 backdrop-blur border border-zinc-800 px-3 py-1 rounded text-[10px] font-mono text-[#c17c0c] animate-bounce delay-700">
                      H3_INDEX: 89a88...
                   </div>
                   <div className="absolute bottom-10 left-10 bg-black/80 backdrop-blur border border-zinc-800 px-3 py-1 rounded text-[10px] font-mono text-zinc-400 animate-bounce">
                      VOTE_SHARE: 32%
                   </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ModuleSection />
        <AudienceSection />
        <BonusSection />
        <WaitlistSection />
      </main>

      <footer className="py-12 border-t border-zinc-900 px-4 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center space-x-4">
            <img src="https://legislativo.pro/img/logo.webp" alt="Guru.voto" className="h-10 grayscale hover:grayscale-0 transition-all cursor-pointer" />
            <div className="h-8 w-px bg-zinc-800"></div>
            <p className="text-zinc-500 text-sm font-mono">© 2026 ELECTION INTELLIGENCE OPS</p>
          </div>
          <div className="flex flex-wrap justify-center space-x-6 text-zinc-500 text-xs uppercase tracking-widest font-mono">
            <button onClick={() => openModal('reference', 'Referência Estratégica')} className="hover:text-[#c17c0c] transition-colors focus:outline-none mb-2 md:mb-0">Referência</button>
            <button onClick={() => openModal('terms', 'Termos de Uso')} className="hover:text-[#c17c0c] transition-colors focus:outline-none mb-2 md:mb-0">Termos de Uso</button>
            <button onClick={() => openModal('lgpd', 'Privacidade & LGPD')} className="hover:text-[#c17c0c] transition-colors focus:outline-none mb-2 md:mb-0">LGPD</button>
            <button onClick={() => openModal('status', 'Monitoramento Global')} className="hover:text-[#c17c0c] transition-colors focus:outline-none flex items-center mb-2 md:mb-0"><span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2 animate-pulse"></span> API Status</button>
          </div>
        </div>
      </footer>

      <LegalModals 
        isOpen={modalConfig.isOpen} 
        onClose={closeModal} 
        title={modalConfig.title} 
        type={modalConfig.type} 
      />
    </div>
  );
};

export default App;
